import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ReachTooltip } from '@openx/components-segment/lib/components/ReachTooltip/ReachTooltip.tsx';
import { convertToPercent } from '@openx/utils/lib/convertToPercent.ts';
import { useTranslation } from 'react-i18next';
import { hasSegmentFee } from '../../../../../../utils/hasSegmentFee.ts';
import { SegmentCategory } from '../../../../hooks/types.ts';
import type { BasicInfoProps } from '../types.ts';
import { isLookalikeSegmentProps, isPricedSegmentProps } from '../utils.ts';
import { basicInfoBoxStyles, labelStyles, labelValuePairStyles } from './styles.ts';

export const BasicInfo = (props: BasicInfoProps) => {
  const { t } = useTranslation();
  const { id, reach, displayReach, organizationName, category, isPredefinedSegment } = props;

  const isLookalike = isLookalikeSegmentProps(props);
  const isPriced = isPricedSegmentProps(props);

  const pricePom = isPriced ? props.pricePom : undefined;
  const priceCpm = isPriced ? props.priceCpm : undefined;
  const lookalikeReachRange = isLookalike ? props.lookalikeReachRange : null;
  const isSelectedList = isLookalike ? props.isSelectedList : false;
  const isSeed = isLookalike ? props.isSeed : false;
  const providerName = isLookalike ? props.providerName : props.providerName;

  const shouldDisplayPrice =
    !isLookalike && category !== SegmentCategory.PREDEFINED && isPriced && hasSegmentFee(pricePom, priceCpm);
  const priceDisplay = shouldDisplayPrice
    ? `${convertToPercent(pricePom ?? 0)}% / $${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(priceCpm ?? 0)}`
    : null;
  const displayReachRange = !isSelectedList && isLookalike && lookalikeReachRange;
  const displayReachValue = !isLookalike || isSelectedList;

  return (
    <Box sx={basicInfoBoxStyles}>
      <Box sx={labelValuePairStyles} data-test="reach-info">
        <Typography variant="body2" sx={labelStyles}>
          {t('Reach:')}
        </Typography>

        {displayReachRange && <Typography variant="body2">{lookalikeReachRange}</Typography>}

        {displayReachValue && <Typography variant="body2">{displayReach}</Typography>}

        {!!reach && !isPredefinedSegment && <ReachTooltip removeMargin id={id} reach={reach} />}
      </Box>

      {priceDisplay && (
        <Box sx={labelValuePairStyles} data-test="price-info">
          <Typography variant="body2" sx={labelStyles}>
            {t('Price:')}
          </Typography>

          <Typography variant="body2">{priceDisplay}</Typography>
        </Box>
      )}

      {!isSeed && (
        <Box sx={labelValuePairStyles} data-test="data-partner-info">
          <Typography variant="body2" sx={labelStyles}>
            {t('Data Partner:')}
          </Typography>

          <Typography variant="body2">
            {organizationName === 'OpenAudience' || isPredefinedSegment
              ? 'OpenXSelect'
              : (organizationName ?? t('N / A'))}
          </Typography>
        </Box>
      )}

      {isLookalike && !isSeed && (
        <Box sx={labelValuePairStyles} data-test="data-provider-info">
          <Typography variant="body2" sx={labelStyles}>
            {t('Data Provider:')}
          </Typography>

          <Typography variant="body2">{providerName || t('N / A')}</Typography>
        </Box>
      )}
    </Box>
  );
};
